import { Elements } from '@stripe/react-stripe-js';
import { useQuery, type UseMutationResult } from '@tanstack/react-query';
import { useEffect } from 'react';
import { getCart } from '@apiCalls';
import { handleRenderError, withErrorBoundary } from '@utils/errorHandler';
import { getStripeOptions, getStripePromise } from '@utils/loadStripe';
import tracker from '@utils/tracking';
import StripeCardForm from './StripeCardForm';
import { type ITicketState } from '@contexts';

export const InjectedStripeCardForm = ({
  stripePublicKey,
  clientSecret,
  paymentRequestMutation,
  cartId,
  event,
  eventSeries,
  tickets,
  promoCode,
}: {
  cartId: string;
  event: IEvent;
  eventSeries: IEventSeries | undefined;
  stripePublicKey: string;
  clientSecret: string;
  promoCode?: string;
  tickets: ITicketState['tickets'];
  paymentRequestMutation: UseMutationResult<
    ICreatePaymentIntentResponse | undefined,
    Error,
    void,
    unknown
  >;
}) => {
  try {
    const { data: cartData } = useQuery({
      queryKey: ['cart', cartId],
      queryFn: () => getCart(cartId),
      enabled: !!cartId,
    });

    useEffect(
      withErrorBoundary(() => {
        if (cartData && tickets && tickets[0]) {
          tracker.trackBeginCheckout(
            cartData,
            tickets[0].Currency,
            event,
            promoCode
          );
        }
      }),
      [cartData, tickets]
    );

    if (clientSecret) {
      return (
        <Elements
          key={clientSecret}
          options={getStripeOptions(clientSecret, event, eventSeries)}
          stripe={getStripePromise(stripePublicKey)}
        >
          <StripeCardForm />
        </Elements>
      );
    } else if (paymentRequestMutation.error) {
      return (
        <p className='error-message'>
          There was an error initiating the payment. Please try again later or
          contact us.
        </p>
      );
    } else {
      return (
        <p className='error-message appCheckoutStepHeading'>
          Setting Up Secure Payment...
        </p>
      );
    }
  } catch (error) {
    return handleRenderError(error);
  }
};
